<template>
    <div class="Login">
        <div
            v-if="getShowAuthenticatedView"
            class="HeaderUserInfoContent"
        >
            Welcome {{ userInitials }}
            <button
                type="button"
                @click="logout()"
            >
                Logout
            </button>
        </div>
        <div
            v-else-if="showSignedInLoaderDisplayed"
            class="SignedIn Container"
        >
            <div class="SignedIn-loaderWrapper">
                <span class="SignedIn-loader">
                    <span
                        class="SignedIn-loaderCircleBack"
                        :style="{ background: getSecondaryColor }"
                    />
                    <span
                        class="SignedIn-loaderCircleFront"
                        :style="{ background: getPrimaryColor }"
                    />
                </span>
            </div>
            <p>{{ getLocale('login_widget:retrieving_user_infos') }}</p>
        </div>
        <div v-else-if="getShowLoginLink">
            <a :href="getLoginFormUrl">Connexion</a>
        </div>
    </div>
</template>

<script>
import * as actionTypes from '../store/action-types.js'
import { mapState } from 'vuex'
import * as mutationTypes from '../store/mutation-types'
import emitter from '../utils/global-emitter'
import LoginManager from '../managers/LoginManager'
import { isEmpty } from '../utils/global-utils'

export default {
    name: 'Login',
    props: {
        additionalInfoDomain: {
            type: String,
            default: ''
        },
        apiKey: {
            type: String,
            default: ''
        },
        checkAccount: {
            type: Boolean,
            default: false
        },
        cookiesDomain: {
            type: String,
            default: ''
        },
        environment: {
            type: String,
            default: 'prod',
            validator: (val) => ['dev', 'dev2', 'local', 'preprod', 'prod', 'qa'].includes(val)
        },
        isAdditionalInfoRequired: {
            type: Boolean,
            default: false
        },
        isSignedInLoaderDisplayed: {
            type: Boolean,
            default: true
        },
        lang: {
            type: String,
            default: 'fr'
        },
        loginContext: {
            type: String,
            default: 'integrated',
            validator: (val) => ['autonomous', 'integrated', 'partial'].includes(val)
        },
        loginMode: {
            type: String,
            default: 'kas',
            validator: (val) => ['kas', 'legacy'].includes(val)
        },
        networkId: {
            type: Number,
            required: true
        },
        primaryColor: {
            type: String,
            default: '#0d3082'
        },
        secondaryColor: {
            type: String,
            default: '#aaaaaa'
        },
        serverSideStatus: {
            type: String,
            default: ''
        },
        signedInCheckPath: {
            type: String,
            default: '/signed'
        },
        syncLoginUrl: {
            type: String,
            default: ''
        },
        syncLogoutUrl: {
            type: String,
            default: ''
        },
        redirectUrl: {
            type: String,
            default: ''
        },
        syncRefreshUrl: {
            type: String,
            default: ''
        },
        syncUnregisterUrl: {
            type: String,
            default: ''
        },
        syncUserStatusUrl: {
            type: String,
            default: ''
        },
        tokenRefreshSafetyDelay: {
            type: Number,
            default: 180
        },
        userInfosAttributes: {
            type: String,
            default: ''
        },
        userWebUrl: {
            type: String,
            default: ''
        }
    },
    data () {
        return {
            input: {
                username: '',
                password: ''
            },
            isRedirectMessageDisplayed: false
        }
    },
    computed: {
        ...mapState({
            showUserInfos: state => state.login.showUserInfos,
            authenticated: state => state.login.authenticated,
            userInfos: state => state.login.userInfos,
            loginFormUrl: state => state.login.loginFormUrl
        }),
        getLoginFormUrl () {
            return this.loginFormUrl
        },
        getPrimaryColor () {
            return this.primaryColor
        },
        getSecondaryColor () {
            return this.secondaryColor
        },
        getShowLoginLink () {
            return this.loginMode === 'kas' && this.loginContext === 'autonomous'
        },
        getShowAuthenticatedView () {
            return this.authenticated && this.loginContext === 'autonomous'
        },
        showSignedInLoaderDisplayed () {
            return this.isSignedInLoaderDisplayed && this.isRedirectMessageDisplayed
        },
        userInitials () {
            if (this.authenticated && this.userInfos !== undefined && this.userInfos !== null) {
                return this.userInfos.attributes.firstName.substr(0, 1).ucFirst() + this.userInfos.attributes.lastName.substr(0, 1).ucFirst()
            }
            return ''
        }
    },
    created () {
        // console.log('LOGIN CREATED')
        this.setI18nLanguage(this.lang)
        if (this.apiKey) {
            LoginManager.apiKey = this.apiKey
        }
        LoginManager.checkAccount = !isEmpty(this.checkAccount) ? this.checkAccount : false
        LoginManager.cookiesDomain = !isEmpty(this.cookiesDomain) ? this.cookiesDomain : location.hostname
        LoginManager.additionalInfoDomain = !isEmpty(this.additionalInfoDomain) ? this.additionalInfoDomain : this.cookiesDomain
        LoginManager.isAdditionalInfoRequired = !isEmpty(this.isAdditionalInfoRequired) ? this.isAdditionalInfoRequired : true
        LoginManager.redirectUrl = !isEmpty(this.redirectUrl) ? this.redirectUrl : `${window.location.origin}/${this.$i18n.locale}/signedin`
        LoginManager.serverSideStatus = this.serverSideStatus
        LoginManager.store = this.$store
        LoginManager.tokenRefreshSafetyDelay = this.tokenRefreshSafetyDelay
        if (!isEmpty(this.userInfosAttributes)) {
            LoginManager.userInfosAttributes = this.userInfosAttributes
        }
        if (this.userWebUrl) {
            LoginManager.userWebUrl = this.userWebUrl
        } else if (this.environment !== 'local') {
            LoginManager.userWebUrl = `https://${this.networkId}.userweb.${this.environment}.instant-system.com`
        } else {
            LoginManager.userWebUrl = 'https://userweb.is'
        }
        LoginManager.init()

        // console.log(this.$store)
        this.$store.commit(`login/${mutationTypes.SET_UNREGISTER_SYNC_URL}`, this.syncUnregisterUrl)
        this.$store.commit(`login/${mutationTypes.SET_LOGIN_CONTEXT}`, this.loginContext)
        this.$store.commit(`login/${mutationTypes.SET_LOGIN_SYNC_URL}`, this.syncLoginUrl)
        this.$store.commit(`login/${mutationTypes.SET_REFRESH_SYNC_URL}`, this.syncRefreshUrl)
        this.$store.commit(`login/${mutationTypes.SET_LOGOUT_SYNC_URL}`, this.syncLogoutUrl)
        this.$store.commit(`login/${mutationTypes.SET_USER_STATUS_SYNC_URL}`, this.syncUserStatusUrl)
        document.body.addEventListener('kas-load-configuration', () => {
            LoginManager.getConfigurationAction()
        })
        document.body.addEventListener('kas-load-configuration-settings', () => {
            if (isEmpty(LoginManager.kasConfigurationSettings)) {
                LoginManager.getConfigurationSettingsAction()
            }
        })
        document.body.addEventListener('kas-login', (event) => {
            // console.log('DOCUMENT BODY KAS LOGIN LISTENER')
            const redirectTo = !isEmpty(event.detail.redirectTo) ? event.detail.redirectTo : null
            // console.log('startLogInProcess IN Login.vue')
            LoginManager.startLogInProcess(redirectTo)
        })
        document.body.addEventListener('kas-refresh-token', (event) => {
            // console.log('DOCUMENT BODY KAS REFRESH TOKEN LISTENER')
            this.$store.dispatch('login/' + actionTypes.REFRESH_TOKEN)
        })
        document.body.addEventListener('kas-logout', (event) => {
            // console.log('DOCUMENT BODY KAS LOGOUT LISTENER')
            this.$store.dispatch('login/' + actionTypes.LOGOUT)
        })
        document.body.addEventListener('kas-unregister', (event) => {
            // console.log('DOCUMENT BODY KAS LOGOUT LISTENER')
            this.$store.dispatch('login/' + actionTypes.UNREGISTER)
        })
        document.body.addEventListener('kas-sync-user-status', (event) => {
            // console.log('DOCUMENT BODY SYNC_USER_STATUS LISTENER')
            if (!isEmpty(this.syncUserStatusUrl)) {
                this.$store.dispatch('login/' + actionTypes.SYNC_USER_STATUS)
            // } else {
            // console.log('sync-user-status-url parameter is empty, you must provide it to use syncUserStatus feature')
            }
        })
        document.body.addEventListener('kas-goto-update-account-page', (event) => {
            // console.log('DOCUMENT BODY KAS GO TO UPDATE ACCOUNT PAGE')
            // console.log('EVENT kas-goto-update-account-page', this.$i18n.locale)
            if (LoginManager.kasConfigurationSettingsPages?.account) {
                window.open(LoginManager.kasConfigurationSettingsPages.account.setUrlParameter('ui_locales', this.$i18n.locale))
            } else {
                window.open(LoginManager.kasConfigurationSettings.pages.account.setUrlParameter('ui_locales', this.$i18n.locale))
            }
        })
        document.body.addEventListener('kas-goto-update-password-page', (event) => {
            // console.log('DOCUMENT BODY KAS GO TO UPDATE PASSWORD PAGE')
            // console.log('EVENT kas-goto-update-password-page', this.$i18n.locale)
            if (LoginManager.kasConfigurationSettingsPages?.password) {
                window.open(LoginManager.kasConfigurationSettingsPages.password.setUrlParameter('ui_locales', this.$i18n.locale))
            } else {
                window.open(LoginManager.kasConfigurationSettings.pages.password.setUrlParameter('ui_locales', this.$i18n.locale))
            }
        })
        document.body.addEventListener('kas-get-user-infos', () => {
            this.$store.dispatch('login/' + actionTypes.GET_USER_INFOS)
        })

        emitter.on('loggedIn', () => {
            // console.log('ON loggedIn')
            this.$store.commit(`login/${mutationTypes.SET_AUTHENTICATED}`, true)
            if (window.location.pathname.indexOf(this.signedInCheckPath) >= 0) {
                if (!isEmpty(this.loginSyncUrl)) {
                    this.$store.dispatch('login/' + actionTypes.SYNC_LOGIN)
                } else {
                    this.redirectAfterLogin()
                }
            } else {
                // console.log('ON EST PAS SUR LA PAGE SIGNED IN')
            }
        })
        emitter.on('unregistered', () => {
            // console.log('ON unregistered')
            this.$store.dispatch('login/' + actionTypes.LOGOUT)
        })
        emitter.on('syncLoginSuccess', () => {
            // console.log('ON syncLoginSuccess')
            // console.log('REDIRECT TO HOMEPAGE')
            this.redirectAfterLogin()
        })
        emitter.on('loggedOut', () => {
            // console.log('ON loggedOut')
            window.location.reload()
            this.$store.commit(`login/${mutationTypes.SET_AUTHENTICATED}`, false)
        })
        if (!isEmpty(this.syncRefreshUrl)) {
            emitter.on('userTokenRefreshed', () => {
                this.$store.dispatch('login/' + actionTypes.SYNC_REFRESH)
            })
        }
    },
    mounted () {
        // console.log('LOGIN MOUNTED')
        // console.log(this.$store)
        // this.$store.dispatch('login/' + actionTypes.INIT_LOGIN)
        this.$store.dispatch('login/' + actionTypes.CHECK_STORED_TOKENS)
        if (window.location.pathname.indexOf(this.signedInCheckPath) >= 0) {
            // console.log('--signedin--')
            this.isRedirectMessageDisplayed = true
            this.getUserToken()
        }
    },
    methods: {
        checkFormError () {
            return (
                this.input.username !== '' &&
                this.input.password !== ''
            )
        },
        login () {
            if (this.checkFormError()) {
                this.$store.dispatch(`login/${actionTypes.LOGIN}`, { data: this.input })
            } else {
                console.log('Error...')
            }
        },
        logout () {
            localStorage.removeItem('userToken')
            localStorage.removeItem('userInfos')
            location.reload()
        },
        updateUsername (value) {
            this.input.username = value
        },
        updatePassword (value) {
            this.input.password = value
        },
        getUserToken () {
            // console.log('--getUserToken--')
            const urlObject = new URL(window.location.href)
            const code = urlObject.searchParams.get('code')
            if (code) {
                // console.log('--get token login.vue---')
                this.$store.dispatch(`login/${actionTypes.GET_USER_TOKEN}`, { code })
            }
        },
        redirectAfterLogin () {
            const redirectTo = LoginManager.getLocalStorageItem('redirectTo')
            if (!isEmpty(redirectTo)) {
                localStorage.removeItem('redirectTo')
                window.location.href = redirectTo
            } else {
                window.location.href = window.location.origin
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.SignedIn {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-family: Lato, Arial, 'Helvetica Neue', Helvetica, sans-serif;

    &-loader {
        display: block;
        position: relative;
        box-sizing: border-box;
        width: 128px;
        height: 128px;
        margin: 15px auto;
        color: #fff;
        animation: rotation 1s linear infinite;

        &CircleFront,
        &CircleBack {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            box-sizing: border-box;
            width: 48px;
            height: 48px;
            transform: scale(.5) translate(0, 0);
            border-radius: 50%;
            background-color: #aaa;
            animation: animloader 1s infinite ease-in-out;
        }

        &CircleBack {
            background-color: #0d3082;
            transform: scale(.5) translate(-48px, -48px);
        }
    }
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes animloader {
    50% {
        transform: scale(1) translate(-50%, -50%);
    }
}
</style>
